import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './TypeAhead.module.scss';
import { Options } from './Options';
import { TextInput } from '@components';
import { InputFieldProps } from '@components/FORM/InputField/InputField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { Spacer, Caption } from '@components';
import { TypeAheadOption, TGroupedOpts, TObject } from '@types';

export interface TypeAheadProps extends InputFieldProps {
	onSelected: (option:TypeAheadOption | string, props?: { group: string }) => void;
	showArrowIcon?: boolean;
	options: TypeAheadOption[] | string[] | TGroupedOpts | null;
	noOptionsText?: string;
	isLoadingOptions?: boolean;
	onClearInput?: (v: string) => void;
	onSelectOpen?: (b: boolean) => void;
	hideNoOptionsText?: boolean;
	multiSelect?: boolean;
	selectedChips?: TypeAheadOption[];
	onChipRemoved?: (option: TypeAheadOption) => void;
	shiftToKeepOpen?: boolean;
	scrollableChips?: boolean;
}

export const TypeAhead = (props:TypeAheadProps) => {
	const {
		id,
		name,
		value,
		onChange,
		onSelected,
		showArrowIcon,
		options = [],
		noOptionsText,
		isLoadingOptions = false,
		onClearInput,
		onSelectOpen,
		placeholder,
		hideNoOptionsText = false,
		multiSelect = false,
		selectedChips = [],
		onChipRemoved,
		shiftToKeepOpen = false,
		scrollableChips = false,
		...otherProps
	} = props;
	const [position, setThePosition] = useState({});
	const [isAboveFold, setIsAboveFold] = useState(true);
	const [selectOpen, setSelectOpen] = useState(false);
	const [showList, setShowList] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const listContainerRef = useRef<HTMLUListElement>(null);
	const txtInputRef = useRef<HTMLInputElement>(null);
	const multiSelectContainerRef = useRef<HTMLDivElement>(null);
	const chipsInputRef = useRef<HTMLDivElement>(null);
	const selectid = useRef<string | null>(null);

	const setSelectid = () => {
		const now = Math.round(new Date(new Date()).getTime());
		selectid.current = `${id ? `${id}-` : ''}${Math.random()}-${now}`;
	}
	const selected = (o:TypeAheadOption | string, p?:{ group: string }, shiftKey = false) => {
		onSelected(o, p);
		if (multiSelect && shiftToKeepOpen) {
			if (shiftKey) {
				// Multi-pick: keep the list open and retain the search term/results.
				setFocusedIndex(-1);
			} else {
				// Single-pick: clear the search and close the list.
				if (onClearInput) onClearInput('');
				setSelectOpen(false);
				setShowList(false);
				if (onSelectOpen) onSelectOpen(false);
			}
		} else if (multiSelect) {
			if (onClearInput) onClearInput('');
			setFocusedIndex(-1);
		} else {
			setSelectOpen(false);
			setShowList(false);
			if (onSelectOpen) onSelectOpen(false);
		}
	}
	const showIcon = () => {
		if (showArrowIcon) {
			return selectOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon
		}
		return undefined;
	}

	const selectableOptions = useMemo(() => {
		if (!options) return [];
		if (Array.isArray(options)) {
			return options.filter((opt) => {
				if (typeof opt === 'object' && opt !== null) {
					return !opt.disabled;
				}
				return true;
			}).map((opt) => ({ option: opt, group: undefined }));
		}
		if (typeof options === 'object') {
			const allOpts: { option: string | TypeAheadOption; group: string; }[] = [];
			for (const [key, value] of Object.entries(options)) {
				value.filter((opt) => {
					if (typeof opt === 'object' && opt !== null) {
						return !opt.disabled;
					}
					return true;
				}).forEach((opt) => {
					allOpts.push({ option: opt, group: key });
				});
			}
			return allOpts;
		}
		return [];
	}, [options]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (multiSelect && e.key === 'Backspace' && !value && selectedChips.length > 0) {
			if (onChipRemoved) onChipRemoved(selectedChips[selectedChips.length - 1]);
			return;
		}

		if (!selectOpen) {
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
				e.preventDefault();
				setSelectOpen(true);
				setFocusedIndex(0);
				if (onSelectOpen) onSelectOpen(true);
			}
			return;
		}

		if (selectableOptions.length === 0) return;

		switch (e.key) {
		case 'ArrowDown':
			e.preventDefault();
			setFocusedIndex((prev) =>
				prev < selectableOptions.length - 1 ? prev + 1 : 0
			);
			break;
		case 'ArrowUp':
			e.preventDefault();
			setFocusedIndex((prev) =>
				prev > 0 ? prev - 1 : selectableOptions.length - 1
			);
			break;
		case 'Enter':
			e.preventDefault();
			if (focusedIndex >= 0 && focusedIndex < selectableOptions.length) {
				const selectedOption = selectableOptions[focusedIndex];
				selected(selectedOption.option, 'group' in selectedOption && selectedOption.group ? { group: selectedOption.group } : undefined, e.shiftKey);
			}
			break;
		case 'Escape':
			e.preventDefault();
			setSelectOpen(false);
			setShowList(false);
			setFocusedIndex(-1);
			if (onSelectOpen) onSelectOpen(false);
			break;
		case 'Tab':
			setSelectOpen(false);
			setShowList(false);
			setFocusedIndex(-1);
			if (onSelectOpen) onSelectOpen(false);
			break;
		default:
			break;
		}
	};

	const focused = () => {
		setSelectOpen(true);
		setFocusedIndex(-1);
		if (onSelectOpen) onSelectOpen(true);
	}
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) onChange(e);
		if (!selectOpen) {
			setSelectOpen(true);
			setFocusedIndex(-1);
			if (onSelectOpen) onSelectOpen(true);
		}
	}
	const handleBlur = () => {
		setSelectOpen(false);
		setShowList(false);
		setFocusedIndex(-1);
		if (onSelectOpen) onSelectOpen(false);
	}
	const setPosition = (aboveFold = isAboveFold) => {
		/* In multiSelect mode the anchor is the outer bordered container, so no offset correction needed.
		   In single mode the anchor is the inner <input> element which sits inside border+padding, so we
		   subtract 11px (1px border + 10px padding) from left and add 22px to width to reach the outer edge. */
		if (multiSelect && multiSelectContainerRef.current && listContainerRef.current) {
			const { offsetHeight, offsetWidth } = multiSelectContainerRef.current;
			const { left, top, bottom } = multiSelectContainerRef.current.getBoundingClientRect();
			const { offsetHeight: oHeight } = listContainerRef.current;
			const posStyles:TObject = {};
			posStyles.position = 'absolute';
			posStyles.left = 0;
			posStyles.top = 0;
			posStyles.width = `${offsetWidth}px`;
			posStyles.minWidth = 'max-content';
			posStyles.willChange = 'transform';
			if (aboveFold) {
				/* Below: cap to the space beneath the container so the list scrolls rather than overflowing the viewport. */
				posStyles.maxHeight = `${window.innerHeight - bottom - 16}px`;
				posStyles.transform = `translate(${left}px, ${top + offsetHeight}px)`;
			} else {
				/* Above: cap to the space above the container (clamp the offset so the list never overflows the top). */
				posStyles.maxHeight = `${top - 16}px`;
				posStyles.transform = `translate(${left}px, ${top - Math.min(oHeight, top - 16)}px)`;
			}
			return posStyles;
		}
		if (txtInputRef.current && listContainerRef.current) {
			const { offsetHeight, offsetWidth } = txtInputRef.current;
			const { left, top } = txtInputRef.current.getBoundingClientRect();
			const { offsetHeight: oHeight } = listContainerRef.current;
			const posStyles:TObject = {};
			posStyles.position = 'absolute';
			posStyles.left = 0;
			posStyles.top = 0;
			posStyles.width = `${offsetWidth + 22}px`;
			posStyles.minWidth = 'max-content';
			posStyles.willChange = 'transform';
			posStyles.maxHeight = `${window.innerHeight - top - 50}px`;
			if (aboveFold) {
				posStyles.transform = `translate(${left - 11}px, ${offsetHeight + 6 + top}px)`;
			} else {
				posStyles.transform = `translate(${left - 11}px, ${top - 6 - oHeight}px)`;
			}
			return posStyles;
		}
		return {}
	}

	const clickOutside = () => {
		setSelectOpen(false);
		if (onSelectOpen) onSelectOpen(false);
	}

	useEffect(() => {
		if (selectOpen && listContainerRef && listContainerRef.current) {
			/* Decide the fold deterministically. In multiSelect the list anchors to the bottom of the
			   height-capped container, so measure the space below the CONTAINER, not the list's own
			   position — measuring the list itself feeds back and flip-flops the menu on every re-render. */
			if (multiSelect && multiSelectContainerRef.current) {
				/* Prefer rendering below; flip above only when there is genuinely more room above.
				   The list is height-capped + scrollable, so it needn't fit its full content below —
				   compare available space, not the list's own height (which caused intermittent flips). */
				const { top, bottom } = multiSelectContainerRef.current.getBoundingClientRect();
				const aboveFold = (window.innerHeight - bottom) >= top;
				setIsAboveFold(aboveFold);
				setThePosition(setPosition(aboveFold));
			} else {
				setIsAboveFold(window.innerHeight - listContainerRef.current.getBoundingClientRect().y - listContainerRef.current.offsetHeight > 0);
				setThePosition(setPosition());
			}
			if (options?.length === 0 && !hideNoOptionsText) {
				setShowList(false)
				return;
			}
			const showListTimeout = setTimeout(() => {
				setShowList(true)
			}, 100);
			return () => clearTimeout(showListTimeout);
		}
	}, [selectOpen, options]);

	useEffect(() => {
		setSelectid();
	}, []);

	useEffect(() => {
		if (scrollableChips && chipsInputRef.current) {
			chipsInputRef.current.scrollTop = chipsInputRef.current.scrollHeight;
		}
	}, [selectedChips, value]);

	const chipLabel = (chip: TypeAheadOption) =>
		typeof chip.label === 'string' ? chip.label : String(chip.value);

	const hasValue = () => value !== '' && value !== null && value !== undefined;

	const renderMultiSelectInput = () => {
		const {
			label,
			required,
			hasError,
			errorMessage,
			keepErrorSpace = true,
			disabled,
			stretch,
			inline,
			charCount,
			charCountLimit = 524288,
			displayThin,
		} = otherProps;

		return (
			<div className={`${styles.multiSelectWrapper} ${stretch ? styles.multiSelectFlex : ''}`}>
				{(label || charCount) && (
					<div className={`${styles.labelsContainer} ${inline ? styles.inline : ''}`}>
						{label && (
							<label className={styles.label}>
								{label}{required && <span className={styles.required}> *</span>}
							</label>
						)}
						{charCount && (
							<p className={styles.charCount}>
								{charCountLimit > 0 ? `${String(value || '').length}/${charCountLimit}` : String(value || '').length}
							</p>
						)}
					</div>
				)}
				<div
					ref={multiSelectContainerRef}
					className={`${styles.multiSelectContainer} ${displayThin ? styles.multiSelectThin : ''} ${disabled ? styles.multiSelectDisabled : ''} ${hasError ? styles.multiSelectError : ''}`}
					onMouseDown={(e) => {
						if (e.target !== txtInputRef.current) {
							e.preventDefault();
							txtInputRef.current?.focus();
						}
					}}
				>
					<div
						ref={chipsInputRef}
						className={`${styles.multiSelectChipsAndInput} ${scrollableChips ? styles.multiSelectScrollable : ''}`}
					>
						{selectedChips.map((chip, i) => (
							<div key={`chip-${i}`} className={styles.chip} data-testid='@Ofload-TypeAhead-Chip'>
								<span className={styles.chipLabel}>{chipLabel(chip)}</span>
								<button
									type='button'
									className={styles.chipRemove}
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => { if (onChipRemoved) onChipRemoved(chip); }}
									aria-label={`Remove ${chipLabel(chip)}`}
								>
									<CloseOutlinedIcon style={{ fontSize: '12px' }} />
								</button>
							</div>
						))}
						<input
							ref={txtInputRef}
							className={styles.multiSelectInput}
							name={name}
							id={selectid.current ?? props.id}
							value={value as string}
							onChange={handleInputChange}
							onFocus={focused}
							onBlur={handleBlur}
							onKeyDown={handleKeyDown}
							placeholder={selectedChips.length ? '' : (placeholder || 'Start typing…')}
							autoComplete='off'
							disabled={disabled}
							data-testid='@Ofload-TextBox'
						/>
					</div>
					<div className={styles.multiSelectIcons}>
						{onClearInput && (hasValue() || selectedChips.length > 0) && (
							<button
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => {
									onClearInput('');
									selectedChips.forEach((chip) => { if (onChipRemoved) onChipRemoved(chip); });
								}}
								className={styles.multiSelectClearButton}
								aria-label='Clear Input'
							>
								<ClearIcon className={styles.multiSelectClearIcon} />
							</button>
						)}
						{showArrowIcon && (
							<span className={styles.multiSelectArrowIcon}>
								{selectOpen
									? <KeyboardArrowUpIcon style={{ fontSize: '24px' }} />
									: <KeyboardArrowDownIcon style={{ fontSize: '24px' }} />
								}
							</span>
						)}
					</div>
				</div>
				{hasError && errorMessage && (
					<div className={styles.errorMsgContainer}>
						<Spacer size={5} />
						<Caption color='var(--txt-error)'>{errorMessage}</Caption>
					</div>
				)}
				{keepErrorSpace && !hasError && <div className={styles.errorMsgContainer} />}
			</div>
		);
	};

	return (
		<>
			{multiSelect ? renderMultiSelectInput() : (
				<TextInput
					{...otherProps}
					name={name}
					value={value}
					onChange={handleInputChange}
					Icon={otherProps.Icon ?? showIcon()}
					iconSize={24}
					placeholder={placeholder || 'Start typing…'}
					inputRef={txtInputRef}
					onFocus={focused}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					id={selectid.current ?? props.id}
					onClearInput={onClearInput}
					autoComplete='off'
				/>
			)}
			{
				selectOpen &&
				!(options?.length === 0 && hideNoOptionsText) &&
				createPortal(<div
					data-testid='typeahead-list-container'
					className={styles.listContainer}
					onMouseDown={(e) => {
						e.preventDefault();
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) clickOutside();
					}}
				>
					<ul ref={listContainerRef} className={`${styles.list} ${showList ? styles.showList : ''}`} id={'typeahead-list'} role={'listbox'} style={position}>
						<Options
							options={options}
							selected={(o:TypeAheadOption | string, p?:{ group: string }, shiftKey?: boolean) => selected(o, p, shiftKey)}
							noOptionsText={noOptionsText}
							isLoadingOptions={isLoadingOptions}
							selectid={selectid.current}
							focusedIndex={focusedIndex}
							multiSelect={multiSelect}
							selectedChipValues={selectedChips.map((c) => c.value)}
						/>
					</ul>
				</div>, document.body)}
		</>
	);
}
